

<template>
  <el-dialog
    v-model="visible"
    :title="t('login.forgot.title')"
    width="min(688px, calc(100vw - 32px))"
    class="enterprise-forgot-password-dialog"
    :append-to-body="true"
    :close-on-click-modal="false"
    @closed="handleClosed"
  >
    <el-form
      ref="forgotFormRef"
      :model="forgotForm"
      :rules="forgotRules"
      label-position="top"
      class="forgot-password-form"
    >
      <el-form-item
        :label="t('login.forgot.labelAccount')"
        prop="account"
      >
        <el-input
          v-model="forgotForm.account"
          :placeholder="t('login.forgot.placeholderAccount')"
          clearable
        />
      </el-form-item>

      <el-form-item
        :label="t('login.forgot.labelContactPhone')"
        prop="phoneNumber"
      >
        <PhoneInput
          v-model="forgotForm.phoneNumber"
          :placeholder="t('login.forgot.placeholderContactPhone')"
          class="forgot-phone-input"
          @keyup.enter="handleGetSmsCode"
        >
          <template #append>
            <el-button
              class="sms-code-btn"
              :disabled="smsCodeCountdown > 0"
              :loading="smsCodeLoading"
              @click="handleGetSmsCode"
            >
              {{ smsCodeButtonText }}
            </el-button>
          </template>
        </PhoneInput>
      </el-form-item>

      <el-form-item
        :label="t('login.forgot.labelSmsCode')"
        prop="smsCode"
      >
        <el-input
          v-model="forgotForm.smsCode"
          :placeholder="t('login.forgot.placeholderSmsCode')"
          clearable
          maxlength="6"
          @keyup.enter="handleConfirmRetrieve"
        />
      </el-form-item>

      <p class="retrieve-password-tip">
        {{ t('login.forgot.tip') }}
      </p>

      <el-button
        type="primary"
        class="confirm-retrieve-btn"
        :loading="submitLoading"
        @click="handleConfirmRetrieve"
      >
        {{ t('login.forgot.submit') }}
      </el-button>
    </el-form>
  </el-dialog>
</template>


<script setup>
import { computed, nextTick, reactive, shallowRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { getSmsCaptcha } from '@/api/utils/captcha'
import { retrieveEnterprisePassword } from '@/api/login'
import PhoneInput from '@/components/PhoneInput/index.vue'
import { GLOBAL_PHONE_PATTERN } from '@/utils/idValidator'

const COUNTDOWN_TIME = 60
const { t } = useI18n()

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  initialAccount: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'success'])

const visible = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value)
})

const forgotFormRef = shallowRef(null)
const smsCodeLoading = shallowRef(false)
const submitLoading = shallowRef(false)
const smsCodeCountdown = shallowRef(0)
const smsCodeTimer = shallowRef(null)

const forgotForm = reactive({
  account: '',
  phoneNumber: '',
  smsCode: ''
})

const validatePhone = (rule, value, callback) => {
  if (!value) {
    callback(new Error(t('login.forgot.validation.phoneRequired')))
  } else if (!GLOBAL_PHONE_PATTERN.test(value)) {
    callback(new Error(t('login.forgot.validation.phoneInvalid')))
  } else {
    callback()
  }
}

const forgotRules = computed(() => ({
  account: [
    { required: true, message: t('login.forgot.validation.accountRequired'), trigger: 'blur' }
  ],
  phoneNumber: [
    { required: true, validator: validatePhone, trigger: 'blur' }
  ],
  smsCode: [
    { required: true, message: t('login.forgot.validation.smsRequired'), trigger: 'blur' },
    { len: 6, message: t('login.forgot.validation.smsLen'), trigger: 'blur' }
  ]
}))

const smsCodeButtonText = computed(() =>
  smsCodeCountdown.value > 0
    ? t('login.forgot.smsRetryAfter', { n: smsCodeCountdown.value })
    : t('login.forgot.smsSend')
)

watch(
  () => props.modelValue,
  async value => {
    if (!value) {
      clearSmsCodeTimer()
      return
    }
    resetForm()
    forgotForm.account = props.initialAccount || ''
    await nextTick()
    forgotFormRef.value?.clearValidate()
  }
)

function resetForm() {
  forgotForm.account = ''
  forgotForm.phoneNumber = ''
  forgotForm.smsCode = ''
}

function clearSmsCodeTimer() {
  if (smsCodeTimer.value) {
    clearInterval(smsCodeTimer.value)
    smsCodeTimer.value = null
  }
  smsCodeCountdown.value = 0
}

function startSmsCodeCountdown() {
  clearSmsCodeTimer()
  smsCodeCountdown.value = COUNTDOWN_TIME
  smsCodeTimer.value = setInterval(() => {
    smsCodeCountdown.value -= 1
    if (smsCodeCountdown.value <= 0) {
      clearSmsCodeTimer()
    }
  }, 1000)
}

async function handleGetSmsCode() {
  if (smsCodeCountdown.value > 0 || smsCodeLoading.value) {
    return
  }
  try {
    await forgotFormRef.value?.validateField(['account', 'phoneNumber'])
  } catch {
    return
  }

  smsCodeLoading.value = true
  try {
    const response = await getSmsCaptcha(forgotForm.phoneNumber.trim())
    if (response && response.code === 200) {
      ElMessage.success(t('login.forgot.toast.smsSent'))
      startSmsCodeCountdown()
    }
  } catch (error) {
    console.error('获取找回密码验证码失败:', error)
  } finally {
    smsCodeLoading.value = false
  }
}

async function handleConfirmRetrieve() {
  try {
    await forgotFormRef.value?.validate()
  } catch {
    return
  }

  submitLoading.value = true
  try {
    const response = await retrieveEnterprisePassword(
      forgotForm.account.trim(),
      forgotForm.phoneNumber.trim(),
      forgotForm.smsCode.trim()
    )
    if (response && response.code === 200) {
      ElMessage.success(response.msg || t('login.forgot.toast.success'))
      emit('success')
      visible.value = false
    }
  } catch (error) {
    console.error('找回密码失败:', error)
  } finally {
    submitLoading.value = false
  }
}

function handleClosed() {
  resetForm()
  clearSmsCodeTimer()
  forgotFormRef.value?.clearValidate()
}
</script>


<style lang="scss" scoped>
.forgot-password-form {
  min-height: 360px;
  padding: 12px 56px 20px;

  :deep(.el-form-item) {
    margin-bottom: 24px;

    .el-form-item__label {
      color: #000;
      font-weight: 500;
    }
  }

  .forgot-phone-input {
    height: 40px;

    :deep(.append-slot) {
      .sms-code-btn {
        height: 100%;
        padding: 0 16px;
        border: none;
        border-radius: 0;
        color: #206ef4;
        background: transparent;
        font-size: 14px;
        white-space: nowrap;

        &:hover:not(:disabled) {
          color: #004494;
          background: #f5f7fa;
        }

        &:disabled {
          color: #c0c4cc;
          background: transparent;
        }
      }
    }
  }

  .retrieve-password-tip {
    margin: 0 0 16px;
    font-size: 14px;
    line-height: 22px;
    color: #0d62f3;
    text-align: center;
  }

  .confirm-retrieve-btn {
    display: block;
    width: 180px;
    height: 40px;
    margin: 8px auto 0;
  }
}

@media (max-width: 768px) {
  .forgot-password-form {
    min-height: auto;
    padding: 4px 0 12px;

    .forgot-phone-input {
      :deep(.append-slot .sms-code-btn) {
        padding: 0 10px;
        font-size: 13px;
      }
    }
  }
}
</style>

<style lang="scss">
.enterprise-forgot-password-dialog {
  z-index: 3000 !important;

  .el-dialog__title {
    color: #000 !important;
    font-weight: 600 !important;
  }
}
</style>
